import {
  Children,
  cloneElement,
  isValidElement,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
  type ReactElement,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useSidebarOpenState, sidebarConfig } from '@backstage/core-components';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import { svgIconClasses } from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from 'tss-react/mui';

const ICON_SIZE = 20;
const ICON_SLOT_WIDTH = 40;
const HEADER_HEIGHT = 48;
const CHILD_HEIGHT = 36;
const LEFT_PADDING = 16;

const GUIDE_LINE_X = LEFT_PADDING + ICON_SLOT_WIDTH / 2;
const GUIDE_LINE_BORDER = 2;
const CHILD_LEFT_PADDING = 1;
const ACCENT_COLOR = 'var(--app-accent)';

const useStyles = makeStyles<void, 'iconSlot'>()((theme, _params, classes) => ({
  root: {
    width: '100%',
  },
  rootClosed: {
    width: sidebarConfig.drawerWidthClosed,
    overflow: 'hidden',
  },
  summaryButton: {
    width: '100%',
    height: HEADER_HEIGHT,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: LEFT_PADDING,
    color: 'var(--app-sidebar-fg)',
    opacity: 1,
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      opacity: 1,
      backgroundColor: 'transparent !important',
      color: 'var(--app-sidebar-fg-selected) !important',
    },
    [`&:hover .${classes.iconSlot}`]: {
      color: 'var(--app-sidebar-fg-selected) !important',
    },
  },
  summaryButtonClosed: {
    width: sidebarConfig.drawerWidthClosed,
    justifyContent: 'center',
    paddingLeft: 0,
    paddingRight: 0,
  },
  summaryButtonActive: {
    backgroundColor: ACCENT_COLOR,
    color: 'var(--bui-bg-app)',
    opacity: 1,
    [`& .${classes.iconSlot}`]: {
      color: 'var(--bui-bg-app) !important',
    },
    '&:hover': {
      backgroundColor: `${ACCENT_COLOR} !important`,
      color: 'var(--bui-bg-app) !important',
      opacity: 1,
    },
  },
  iconSlot: {
    width: ICON_SLOT_WIDTH,
    minWidth: ICON_SLOT_WIDTH,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'inherit',
    '& > svg, & > img': {
      width: ICON_SIZE,
      height: ICON_SIZE,
      fontSize: ICON_SIZE,
      color: 'inherit',
    },
    [`& .${svgIconClasses.root}`]: {
      color: 'inherit !important',
      fill: 'currentColor',
    },
  },
  label: {
    flex: 1,
    fontFamily: '"Proxima Nova", ui-sans-serif, system-ui, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '16px',
    color: 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left',
  },
  expandIcon: {
    color: 'inherit',
    fontSize: '16px',
    flexShrink: 0,
    opacity: 0.7,
    transition: 'transform 0.2s ease',
    marginRight: 8,
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },

  childrenWrapper: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 2,
    paddingBottom: 6,
    marginLeft: GUIDE_LINE_X,
    borderLeft: `${GUIDE_LINE_BORDER}px solid ${
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.15)'
        : 'rgba(0,0,0,0.12)'
    }`,
    '& > a[href]:not([aria-current="page"]), & > a[tabindex]:not([aria-current="page"]), & > button[type]:not([aria-current="page"]), & > button:not([type]):not([aria-current="page"])':
      {
        height: `${CHILD_HEIGHT}px !important`,
        minHeight: `${CHILD_HEIGHT}px !important`,
        maxHeight: `${CHILD_HEIGHT}px !important`,
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        paddingLeft: `${CHILD_LEFT_PADDING}px !important`,
        boxSizing: 'border-box',
        borderLeft: 'none !important',
        marginLeft: '0 !important',
        width: '100% !important',
        display: 'flex !important',
        alignItems: 'center !important',
        backgroundColor: 'transparent !important',
        color: 'var(--app-sidebar-fg) !important',
        opacity: 1,
        transition: 'color 0.15s ease',
      },

    '& > a[aria-current="page"], & > button[aria-current="page"]': {
      height: `${CHILD_HEIGHT}px !important`,
      minHeight: `${CHILD_HEIGHT}px !important`,
      maxHeight: `${CHILD_HEIGHT}px !important`,
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
      paddingLeft: `${CHILD_LEFT_PADDING}px !important`,
      boxSizing: 'border-box',
      width: `calc(100% + ${GUIDE_LINE_BORDER}px) !important`,
      display: 'flex !important',
      alignItems: 'center !important',
      backgroundColor: 'transparent !important',
      color: 'var(--app-sidebar-fg) !important',
      borderLeft: `2px solid rgba(0,0,0,0.12)`,
      marginLeft: `-${GUIDE_LINE_BORDER}px !important`,
      opacity: 1,
      transition: 'color 0.15s ease',
    },

    '& > a[href]:hover, & > a[tabindex]:hover, & > button:hover': {
      opacity: 1,
    },
    '& > a svg, & > button svg': {
      width: `${ICON_SIZE}px !important`,
      height: `${ICON_SIZE}px !important`,
      fontSize: `${ICON_SIZE}px !important`,
      flexShrink: 0,
      color: ACCENT_COLOR,
    },
    '& > a[aria-current="page"] svg, & > button[aria-current="page"] svg': {
      color: 'var(--app-sidebar-fg-selected) !important',
    },
    '& > a img, & > button img': {
      width: `${ICON_SIZE}px !important`,
      height: `${ICON_SIZE}px !important`,
      objectFit: 'contain',
      flexShrink: 0,
    },

    '& > a span, & > button span': {
      fontFamily:
        '"Proxima Nova", ui-sans-serif, system-ui, sans-serif !important',
      fontSize: '14px !important',
      fontWeight: '500 !important',
      lineHeight: '16px !important',
    },
  },
  childActive: {
    width: `calc(100% + ${GUIDE_LINE_BORDER}px) !important`,
    borderLeft: `3px solid ${ACCENT_COLOR} !important`,
    marginLeft: `-${GUIDE_LINE_BORDER}px !important`,
    color: 'var(--app-sidebar-fg-selected) !important',
    opacity: '1 !important',
    '& svg': {
      color: 'var(--app-sidebar-fg-selected) !important',
    },
  },
}));

type SidebarAccordionGroupProps = {
  icon: ReactNode;
  label: string;
  children: ReactNode;
  to?: string;
};

const normalizeUrl = (value: string) => {
  if (!value) {
    return '/';
  }
  return value.startsWith('/') ? value : `/${value}`;
};

export const SidebarAccordionGroup = ({
  icon,
  label,
  children,
  to,
}: SidebarAccordionGroupProps) => {
  const { classes, cx } = useStyles();
  const location = useLocation();
  const [expanded, setExpanded] = useState(true);
  const [isSummaryActive, setIsSummaryActive] = useState(false);
  const { isOpen, setOpen } = useSidebarOpenState();

  const currentUrl = normalizeUrl(`${location.pathname}${location.search}`);

  const isChildActive = useCallback(
    (toUrl?: string) => {
      if (!toUrl) {
        return false;
      }
      return currentUrl === normalizeUrl(toUrl);
    },
    [currentUrl],
  );

  useEffect(() => {
    let isActiveChild = false;
    Children.forEach(children, child => {
      if (isValidElement(child) && child.props.to) {
        if (isChildActive(String(child.props.to))) {
          isActiveChild = true;
        }
      }
    });

    const isGroupActive = to
      ? (() => {
          const groupPath = to.split('?')[0].replace(/^\//, '');
          const currentPath = location.pathname.replace(/^\//, '');
          return (
            currentPath === groupPath || currentPath.startsWith(`${groupPath}/`)
          );
        })()
      : false;

    setIsSummaryActive(isGroupActive || isActiveChild);
    if (isActiveChild || isGroupActive) {
      setExpanded(true);
    }
  }, [currentUrl, children, to, isChildActive, location.pathname]);

  useEffect(() => {
    if (!isOpen) {
      setExpanded(false);
    }
  }, [isOpen]);

  const handleClick = () => {
    if (isOpen) {
      setExpanded(prev => !prev);
    } else {
      setOpen(true);
      setExpanded(true);
    }
  };

  return (
    <Box className={cx(classes.root, !isOpen && classes.rootClosed)}>
      <ButtonBase
        onClick={handleClick}
        disableRipple
        className={cx(
          classes.summaryButton,
          !isOpen && classes.summaryButtonClosed,
          isSummaryActive && classes.summaryButtonActive,
        )}
      >
        {isOpen ? (
          <>
            <Box className={classes.iconSlot}>{icon}</Box>
            <Typography className={classes.label}>{label}</Typography>
            <ExpandMoreIcon
              className={cx(
                classes.expandIcon,
                expanded && classes.expandIconOpen,
              )}
            />
          </>
        ) : (
          icon
        )}
      </ButtonBase>

      <Collapse in={expanded && isOpen} timeout="auto" unmountOnExit>
        <Box className={classes.childrenWrapper}>
          {Children.map(children, child => {
            if (!isValidElement(child)) {
              return child;
            }

            const toValue = child.props?.to
              ? String(child.props.to)
              : undefined;
            const active = isChildActive(toValue);

            return cloneElement(child as ReactElement<any>, {
              disableHighlight: true,
              className: cx(
                child.props.className,
                active && classes.childActive,
              ),
            });
          })}
        </Box>
      </Collapse>
    </Box>
  );
};
